import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Autocomplete,
  TextField,
  Typography,
  Chip,
  Stack,
  Paper,
} from '@mui/material';
import { useReactFlow } from '@xyflow/react';
import intl from 'components/i18n/ReactIntlWrapper';
import {
  DataObject as DataIcon,
  History as PreviousIcon,
} from '@mui/icons-material';
import {
  getAvailablePreviousOutputs,
  getParameterReferencePath,
} from '../../../utils/workflowGraphTraversal';
import {
  PreviousNodeOutput,
  ActionTypeOutputField,
} from '../../../types/automaticActions';

interface PreviousOutputSelectorProps {
  currentNodeId: string;
  moduleId?: number;
  selectedNodeId: string | null;
  selectedFieldName: string | null;
  onNodeChange: (nodeId: string | null) => void;
  onFieldChange: (fieldName: string | null) => void;
  onPathChange?: (path: string) => void;
  disabled?: boolean;
}

export const PreviousOutputSelector: React.FC<PreviousOutputSelectorProps> = ({
  currentNodeId,
  moduleId,
  selectedNodeId,
  selectedFieldName,
  onNodeChange,
  onFieldChange,
  onPathChange,
  disabled = false,
}) => {
  const { getNodes, getEdges } = useReactFlow();
  const [previousOutputs, setPreviousOutputs] = useState<PreviousNodeOutput[]>([]);
  const [selectedNode, setSelectedNode] = useState<PreviousNodeOutput | null>(null);

  // Get available previous outputs whenever the graph changes
  useEffect(() => {
    const nodes = getNodes();
    const edges = getEdges();
    
    const outputs = getAvailablePreviousOutputs(
      currentNodeId,
      nodes,
      edges,
      moduleId
    );
    
    setPreviousOutputs(outputs);

    // Update selected node if it exists in the outputs
    if (selectedNodeId) {
      const node = outputs.find(o => o.nodeId === selectedNodeId);
      setSelectedNode(node || null);
    }
  }, [currentNodeId, moduleId, selectedNodeId, getNodes, getEdges]);

  // Get available fields for the selected node
  const availableFields = useMemo(() => {
    return selectedNode?.outputFields || [];
  }, [selectedNode]);

  // Update path when selection changes
  useEffect(() => {
    if (selectedNodeId && selectedFieldName && onPathChange) {
      const path = getParameterReferencePath(
        selectedNodeId,
        selectedFieldName,
        previousOutputs
      );
      onPathChange(path);
    }
  }, [selectedNodeId, selectedFieldName, previousOutputs, onPathChange]);

  const handleNodeChange = (_event: any, value: PreviousNodeOutput | null) => {
    setSelectedNode(value);
    onNodeChange(value?.nodeId || null);
    // Reset field selection when node changes
    onFieldChange(null);
  };

  const handleFieldChange = (_event: any, value: ActionTypeOutputField | null) => {
    onFieldChange(value?.name || null);
  };

  // Get node type icon
  const getNodeTypeIcon = (nodeType: PreviousNodeOutput['nodeType']) => {
    switch (nodeType) {
      case 'automatic':
        return <PreviousIcon fontSize="small" color="primary" />;
      case 'manual':
      case 'start':
        return <DataIcon fontSize="small" color="action" />;
      default:
        return null;
    }
  };

  // Get node type label
  const getNodeTypeLabel = (nodeType: PreviousNodeOutput['nodeType']) => {
    switch (nodeType) {
      case 'automatic':
        return intl.get('workflow.automaticAction.previousOutput.automaticNode');
      case 'manual':
        return intl.get('workflow.automaticAction.previousOutput.manualNode');
      case 'start':
        return intl.get('workflow.automaticAction.previousOutput.entityData');
      default:
        return '';
    }
  };

  if (previousOutputs.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.neutral' }}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          {intl.get('workflow.automaticAction.previousOutput.noPreviousNodes')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      {/* Node Selector */}
      <Autocomplete
        value={selectedNode}
        onChange={handleNodeChange}
        options={previousOutputs}
        getOptionLabel={(option) => option.nodeName}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label={intl.get('workflow.automaticAction.previousOutput.selectNode')}
            placeholder={intl.get('workflow.automaticAction.previousOutput.selectNodePlaceholder')}
            size="small"
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
              {getNodeTypeIcon(option.nodeType)}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2">{option.nodeName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {getNodeTypeLabel(option.nodeType)}
                  {option.actionTypeId && ` • ${option.actionTypeId}`}
                </Typography>
              </Box>
              <Chip
                label={`${option.outputFields.length} ${intl.get('workflow.automaticAction.previousOutput.fields')}`}
                size="small"
                variant="outlined"
              />
            </Stack>
          </Box>
        )}
      />

      {/* Field Selector - Only shown when a node is selected */}
      {selectedNode && (
        <Autocomplete
          value={availableFields.find(f => f.name === selectedFieldName) || null}
          onChange={handleFieldChange}
          options={availableFields}
          getOptionLabel={(option) => `${option.name} (${option.type})`}
          disabled={disabled || !selectedNode}
          renderInput={(params) => (
            <TextField
              {...params}
              label={intl.get('workflow.automaticAction.previousOutput.selectField')}
              placeholder={intl.get('workflow.automaticAction.previousOutput.selectFieldPlaceholder')}
              size="small"
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Stack spacing={0.5} sx={{ width: '100%' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.name}
                  </Typography>
                  <Chip label={option.type} size="small" variant="outlined" />
                </Stack>
                {option.description && (
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
        />
      )}

      {/* Preview of the selected path */}
      {selectedNodeId && selectedFieldName && (
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'primary.lighter' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {intl.get('workflow.automaticAction.previousOutput.selectedPath')}:
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {selectedNode?.nodeName} → {selectedFieldName}
          </Typography>
        </Paper>
      )}
    </Stack>
  );
};

export default PreviousOutputSelector;
